import { useSignedCdnImage } from './chunk-3JVPQKNG.js';
import { cn } from './chunk-FFX3LZW7.js';
import * as React from 'react';
import { Loader2, ImagePlus, X } from 'lucide-react';
import { validateLogoFile } from '@authe/fe-commons/assets';
import { jsx, jsxs } from 'react/jsx-runtime';

function LogoUpload({
  value,
  onChange,
  onUpload,
  validate,
  accept = "image/png,image/jpeg,image/webp,image/svg+xml",
  disabled,
  hint = "PNG, SVG, WebP or JPG up to 512 KB",
  id,
  className
}) {
  const inputRef = React.useRef(null);
  const [status, setStatus] = React.useState("idle");
  const [error, setError] = React.useState(null);
  const [dragging, setDragging] = React.useState(false);
  const handleFile = React.useCallback(
    async (file) => {
      if (!file) return;
      const check = validateLogoFile(file, validate);
      if (!check.ok) {
        setStatus("error");
        setError(check.error ?? "Invalid file");
        return;
      }
      setStatus("uploading");
      setError(null);
      try {
        const result = await onUpload(file);
        onChange(result);
        setStatus("idle");
      } catch (e) {
        setStatus("error");
        setError(e instanceof Error ? e.message : "Upload failed");
      }
    },
    [onChange, onUpload, validate]
  );
  const preview = useSignedCdnImage(value?.url ?? value?.key);
  const previewUrl = preview.src;
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-1.5", className), children: /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex items-center gap-3 rounded-lg border border-dashed p-3 transition-colors",
        dragging && "border-brand bg-brand/5",
        status === "error" && "border-destructive/50",
        disabled && "opacity-50"
      ),
      onDragOver: (e) => {
        if (disabled) return;
        e.preventDefault();
        setDragging(true);
      },
      onDragLeave: () => setDragging(false),
      onDrop: (e) => {
        if (disabled) return;
        e.preventDefault();
        setDragging(false);
        void handleFile(e.dataTransfer.files?.[0]);
      },
      children: [
        /* @__PURE__ */ jsxs("div", { className: "relative flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted", children: [
          status === "uploading" ? /* @__PURE__ */ jsx(Loader2, { className: "size-5 animate-spin text-muted-foreground" }) : previewUrl ? /* @__PURE__ */ jsx("img", { src: previewUrl, crossOrigin: preview.crossOrigin, alt: "Logo preview", className: "size-full object-cover" }) : /* @__PURE__ */ jsx(ImagePlus, { className: "size-5 text-muted-foreground" }),
          value?.key && status !== "uploading" && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "aria-label": "Remove logo",
              onClick: () => {
                onChange(null);
                setStatus("idle");
                setError(null);
              },
              className: "absolute right-0 top-0 rounded-bl-md bg-background/90 p-0.5 text-muted-foreground hover:text-destructive",
              children: /* @__PURE__ */ jsx(X, { className: "size-3.5" })
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              id,
              type: "button",
              disabled: disabled || status === "uploading",
              onClick: () => inputRef.current?.click(),
              className: "rounded-md border px-2.5 py-1 text-sm hover:bg-accent disabled:opacity-50",
              children: previewUrl ? "Replace" : "Upload logo"
            }
          ),
          /* @__PURE__ */ jsx("p", { className: cn("mt-1 truncate text-xs", error ? "text-destructive" : "text-muted-foreground"), children: error ?? hint })
        ] }),
        /* @__PURE__ */ jsx(
          "input",
          {
            ref: inputRef,
            type: "file",
            accept,
            className: "hidden",
            disabled,
            onChange: (e) => {
              void handleFile(e.target.files?.[0]);
              e.target.value = "";
            }
          }
        )
      ]
    }
  ) });
}

export { LogoUpload };
//# sourceMappingURL=chunk-3LK2JY6U.js.map
//# sourceMappingURL=chunk-3LK2JY6U.js.map